import type { BadgeColor } from '@ksyos/design-system';
import { DateTime } from '@ksyos/date-time';
import {
    Alert,
    Badge,
    Box,
    Button,
    Card,
    Col,
    Form,
    FormButtonRow,
    FormGroup,
    IconButton,
    Row,
    Text,
    TextInput,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { isDateInBetweenTwoDates } from '@common/utils/dates';
import { insurerAddEditSchema } from '@common/validation/insurer';
import { FormParagraphFormGroup } from '@frontend/components/fields/FormParagraphField';
import { translateKey } from '@frontend/translate';
import {
    assertHasAuthorization,
    emptyValidationErrors,
    getValidationErrorTranslation,
    useAbortableFetch,
    validate,
} from '@frontend/utils/';
import type { IInsurer } from './InsurerAddEdit';
import { DateInput } from '../../fields/Date';
import { CancelButton } from '../../userDataManagement/CancelButton';
import { FormattedDate } from '../../userDataManagement/FormattedDate';
import { SaveButton } from '../../userDataManagement/SaveButton';

interface IInsurerTurnoverCeilingProps {
    handleChange: <T = any>(values: Partial<T>) => Promise<void>;
    insurer: IInsurer;
}

interface ITurnoverCeilingInformation {
    endDate: string | null;
    phoneNumberCareMediation: string | null;
    startDate: string | null;
}

export function InsurerTurnoverCeiling(props: IInsurerTurnoverCeilingProps) {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [errors, setErrors] = useState(emptyValidationErrors);
    const abortableFetch = useAbortableFetch();
    const [turnoverCeilingInformation, setTurnoverCeilingInformation] = useState<ITurnoverCeilingInformation>({
        startDate: null,
        endDate: null,
        phoneNumberCareMediation: null,
    });

    assertHasAuthorization(ManagerAuthorization.insurersAddEdit);

    useEffect(() => {
        if (
            props.insurer.turnoverCeilingEndDate &&
            DateTime.now().subtract(1, 'day').isAfter(props.insurer.turnoverCeilingEndDate)
        ) {
            setTurnoverCeilingInformation({
                startDate: null,
                endDate: null,
                phoneNumberCareMediation: null,
            });
            props.handleChange({
                turnoverCeilingStartDate: null,
                turnoverCeilingEndDate: null,
                phoneNumberCareMediation: null,
            });
        } else {
            setTurnoverCeilingInformation({
                startDate: props.insurer.turnoverCeilingStartDate,
                endDate: props.insurer.turnoverCeilingEndDate,
                phoneNumberCareMediation: props.insurer.phoneNumberCareMediation,
            });
        }
    }, [props.insurer]);

    useEffect(() => {
        if (isEditing) {
            setTurnoverCeilingInformation({
                startDate: props.insurer.turnoverCeilingStartDate ?? DateTime.now().format('YYYY-MM-DD'),
                endDate: props.insurer.turnoverCeilingEndDate ?? DateTime.now().endOf('year').format('YYYY-MM-DD'),
                phoneNumberCareMediation: props.insurer.phoneNumberCareMediation,
            });
        }
    }, [isEditing]);

    return (
        <Card>
            <Card.Transition type={isEditing ? 'edit' : 'read'} />
            <Card.Header
                action={
                    !isEditing && turnoverCeilingInformation.startDate && turnoverCeilingInformation.endDate ? (
                        <IconButton
                            icon="edit-light"
                            labelText="edit"
                            onClick={() => setIsEditing(true)}
                        />
                    ) : undefined
                }
                title={translateKey('insurers.careLimitation')}
            />
            <Card.Body>
                {!isEditing && readForm()}
                {isEditing && editForm()}
            </Card.Body>
        </Card>
    );

    function readForm() {
        return (
            <>
                {!turnoverCeilingInformation.startDate && !turnoverCeilingInformation.endDate && (
                    <>
                        <Text>
                            Er is geen zorgbeperking ingesteld. Alle zorg kan geleverd worden aan de patiënten verzekerd
                            bij deze verzekeraar.
                        </Text>
                        <Button
                            iconLeft="add"
                            onClick={() => setIsEditing(true)}
                            sx={{ mt: 6 }}
                            variant="link"
                        >
                            Periode van beperkte zorg instellen
                        </Button>
                    </>
                )}
                {turnoverCeilingInformation.startDate && turnoverCeilingInformation.endDate && (
                    <>
                        <FormGroup label={translateKey('insurers.periodOfCareLimitation')}>
                            <Box
                                display="flex"
                                flexWrap="wrap"
                                gap={2}
                            >
                                <FormattedDate value={turnoverCeilingInformation.startDate} />
                                <Text
                                    as="small"
                                    size="xs"
                                    sx={{ mt: [1, null, 2] }}
                                >
                                    {translateKey('common.until')}
                                </Text>
                                <FormattedDate value={turnoverCeilingInformation.endDate} />
                                {getBadge()}
                            </Box>
                        </FormGroup>
                        {turnoverCeilingInformation.phoneNumberCareMediation && (
                            <FormParagraphFormGroup label={translateKey('insurers.phoneForCareMediation')}>
                                {turnoverCeilingInformation.phoneNumberCareMediation ?? ''}
                            </FormParagraphFormGroup>
                        )}
                        {getInfoParagraph()}
                        <Alert
                            appearance="info"
                            className="mv-0"
                        >
                            {translateKey('insurers.ensureTeamLeadsAreAware')}
                        </Alert>
                    </>
                )}
            </>
        );
    }

    function editForm() {
        return (
            <Form submitOnEnter={false}>
                <FormGroup label={translateKey('insurers.periodOfCareLimitation')}>
                    <Row
                        columnGap={[2, null, 4]}
                        rowGap={2}
                    >
                        <Col span={[12, 5]}>
                            <Box>
                                <DateInput
                                    error={errors.get('startDate')}
                                    minDate={DateTime.now().format('YYYY-MM-DD')}
                                    onChange={(value) => handleChange('startDate', value)}
                                    size="large"
                                    value={turnoverCeilingInformation.startDate}
                                    variant="outline"
                                />
                            </Box>
                        </Col>
                        <Col span={[12, 1]}>
                            <Text
                                as="small"
                                mt={[0, 3]}
                                size="xs"
                                textAlign="center"
                            >
                                {translateKey('common.until')}
                            </Text>
                        </Col>
                        <Col span={[12, 5]}>
                            <Box>
                                <DateInput
                                    error={
                                        turnoverCeilingInformation.startDate !== null &&
                                        turnoverCeilingInformation.endDate === null
                                            ? 'koi_endDate'
                                            : ''
                                    }
                                    minDate={DateTime.now().format('YYYY-MM-DD')}
                                    onChange={(value) => handleChange('endDate', value)}
                                    size="large"
                                    value={turnoverCeilingInformation.endDate}
                                    variant="outline"
                                />
                            </Box>
                        </Col>
                        <Col span={[12, 1]}>
                            <Box
                                display="flex"
                                justifyContent={['center', 'flex-end']}
                            >
                                <IconButton
                                    icon="delete"
                                    onClick={() => clearTurnoverCeilingInformation()}
                                />
                            </Box>
                        </Col>
                    </Row>
                </FormGroup>
                <FormGroup
                    isRequired={true}
                    label={translateKey('insurers.phoneForCareMediation')}
                >
                    <TextInput
                        error={getValidationErrorTranslation(getTelephoneNumberError())}
                        onChange={(e) => handleChange('phoneNumberCareMediation', e.target.value)}
                        required
                        value={turnoverCeilingInformation.phoneNumberCareMediation ?? ''}
                    />
                </FormGroup>
                {getInfoParagraph()}
                <FormButtonRow>
                    <CancelButton
                        disabled={isSaving}
                        onCancel={async () => handleCancel()}
                    />
                    <SaveButton
                        disabled={isSaving}
                        onSave={async () => await handleSubmit()}
                    />
                </FormButtonRow>
            </Form>
        );
    }

    function getTelephoneNumberError() {
        const error = errors.get('phoneNumberCareMediation');
        if (error === 'string_base') {
            return 'onePhoneNumberRequired';
        } else if (error === 'string_pattern.base') {
            return 'invalidPhoneNumber';
        } else if (error === 'any_only') {
            return 'phoneNumberCareMediationWithoutDate';
        }

        return error;
    }

    function handleChange(key: keyof ITurnoverCeilingInformation, value: any) {
        setTurnoverCeilingInformation({ ...turnoverCeilingInformation, [key]: value });
    }

    function clearTurnoverCeilingInformation() {
        setTurnoverCeilingInformation({ phoneNumberCareMediation: null, startDate: null, endDate: null });
    }

    function getBadge() {
        let color: BadgeColor = 'neutral';
        let text = 'Gepland';
        if (
            isDateInBetweenTwoDates(
                DateTime.now().format('YYYY-MM-DD'),
                turnoverCeilingInformation.startDate,
                turnoverCeilingInformation.endDate
            )
        ) {
            text = 'Nu geldend';
            color = 'green';
        }

        return (
            <Badge
                color={color}
                size="medium"
                sx={{ mt: [0, null, 1] }}
                text={text}
            />
        );
    }

    function getInfoParagraph() {
        return (
            <Text mt={6}>
                In deze periode van beperkte zorg kan alleen de ongecontracteerde-, al bekostigde- en alternatief
                bekostigde zorg gestart worden. Nieuwe gecontracteerde zorg kan tijdelijk niet gestart worden.
            </Text>
        );
    }

    function handleCancel() {
        setIsEditing(false);
        setErrors(emptyValidationErrors);
        setTurnoverCeilingInformation({
            startDate: props.insurer.turnoverCeilingStartDate,
            endDate: props.insurer.turnoverCeilingEndDate,
            phoneNumberCareMediation: props.insurer.phoneNumberCareMediation,
        });
    }

    async function handleSubmit() {
        const url = `/api/insurers/${props.insurer.id}`;

        const validationResult = validate(insurerAddEditSchema, {
            ...props.insurer,
            turnoverCeilingStartDate: turnoverCeilingInformation.startDate,
            turnoverCeilingEndDate: turnoverCeilingInformation.endDate,
            phoneNumberCareMediation: turnoverCeilingInformation.phoneNumberCareMediation || null,
        });

        setErrors(validationResult.errors);
        if (validationResult.hasErrors) {
            return;
        }

        setIsSaving(true);
        await props.handleChange({
            turnoverCeilingStartDate: turnoverCeilingInformation.startDate,
            turnoverCeilingEndDate: turnoverCeilingInformation.endDate,
            phoneNumberCareMediation: turnoverCeilingInformation.phoneNumberCareMediation,
        });
        await abortableFetch.fetchJson(url, 'POST', validationResult.item);
        setIsEditing(false);
        setIsSaving(false);
    }
}
